import { Node as PmNode } from 'prosemirror-model'
import { EditorState, Plugin, PluginKey } from 'prosemirror-state'

import { NodeTypeMap } from '@showrunner/codex'

export const actSlugTrackerKey = new PluginKey<DocumentRanges>(
  'act-slug-tracker',
)

export function actSlugTrackerPlugin() {
  return new Plugin<DocumentRanges>({
    key: actSlugTrackerKey,
    state: {
      init(_, state) {
        return new DocumentRanges(state.doc)
      },
      apply(_tr, pluginState, oldState, newState) {
        if (oldState.doc.eq(newState.doc)) return pluginState
        return new DocumentRanges(newState.doc)
      },
    },
  })
}

export function getDocumentRanges(state: EditorState) {
  return actSlugTrackerKey.getState(state)
}

interface ActSlugRange {
  startPos: number
  endPos: number
  act?: string
  slug?: string
}

interface SceneRange {
  startPos: number
  endPos: number
  node: PmNode
}

export class DocumentRanges {
  private readonly ranges: ActSlugRange[] = []
  private readonly sceneRanges: SceneRange[] = []

  constructor(readonly doc: PmNode) {
    this.buildRanges(doc)
  }

  private buildRanges(doc: PmNode) {
    let currentAct: string | undefined
    let currentSlug: string | undefined
    let rangeStart = 0
    let rangeStarted = false

    let currentSceneNode: PmNode | undefined
    let sceneRangeStart: number | undefined

    doc.descendants((node, pos) => {
      const nodeType = node.type.name
      const isActOrSlug =
        nodeType === NodeTypeMap.NEW_ACT || nodeType === NodeTypeMap.SLUG

      const isSectionBoundary =
        nodeType === NodeTypeMap.NEW_ACT ||
        nodeType === NodeTypeMap.SLUG ||
        nodeType === NodeTypeMap.SCENE_HEADING

      if (isActOrSlug) {
        // Save previous range if one was started
        if (rangeStarted) {
          this.ranges.push({
            startPos: rangeStart,
            endPos: pos - 1,
            act: currentAct,
            slug: currentSlug,
          })
        }

        if (nodeType === NodeTypeMap.NEW_ACT) {
          currentAct = node.textContent.trim()
          currentSlug = undefined // Reset slug on new act
        } else if (nodeType === NodeTypeMap.SLUG) {
          currentSlug = node.textContent.trim()
        }

        rangeStart = pos
        rangeStarted = true
      }

      if (isSectionBoundary) {
        // End current scene range if one exists
        if (currentSceneNode && sceneRangeStart !== undefined) {
          this.sceneRanges.push({
            startPos: sceneRangeStart,
            endPos: pos - 1,
            node: currentSceneNode,
          })
        }

        // Start new scene only if this is a scene heading
        if (nodeType === NodeTypeMap.SCENE_HEADING) {
          currentSceneNode = node
          sceneRangeStart = pos
        } else {
          // Act or slug ends scene but doesn't start a new one
          currentSceneNode = undefined
          sceneRangeStart = undefined
        }
      }

      return true // continue traversing
    })

    // Don't forget the final ranges
    if (rangeStarted) {
      this.ranges.push({
        startPos: rangeStart,
        endPos: doc.nodeSize,
        act: currentAct,
        slug: currentSlug,
      })
    }

    if (currentSceneNode && sceneRangeStart !== undefined) {
      this.sceneRanges.push({
        startPos: sceneRangeStart,
        endPos: doc.nodeSize,
        node: currentSceneNode,
      })
    }
  }

  // Binary search to find range at the given position
  getActAndSlugAt(pos: number): { act?: string; slug?: string } {
    let left = 0
    let right = this.ranges.length - 1
    let result = -1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)

      if (this.ranges[mid].startPos <= pos) {
        result = mid
        left = mid + 1
      } else {
        right = mid - 1
      }
    }

    if (result >= 0 && pos <= this.ranges[result].endPos) {
      return {
        act: this.ranges[result].act,
        slug: this.ranges[result].slug,
      }
    }

    return {}
  }

  /**
   * Get the scene associated with the given document position
   * @param pos index offset to document
   * @returns the scene heading node
   */
  getSceneAt(pos: number): PmNode | undefined {
    let left = 0
    let right = this.sceneRanges.length - 1
    let result = -1

    while (left <= right) {
      const mid = Math.floor((left + right) / 2)

      if (this.sceneRanges[mid].startPos <= pos) {
        result = mid
        left = mid + 1
      } else {
        right = mid - 1
      }
    }

    if (result >= 0 && pos <= this.sceneRanges[result].endPos) {
      return this.sceneRanges[result].node
    }

    return undefined
  }
}
